import React, { Dispatch, useRef } from 'react'
import { Button, Pagination } from 'react-bootstrap'

import { usePdfViewer } from 'hooks/usePdfViewer'
import Annotation from 'react-image-annotation'
import { Annotation as IAnnotation } from 'types'
import { AnnotationEditor, AnnotationEditorProps } from './AnnotationEditor'
import { styles } from './styles'

export interface EditorProps {
  addAnnotation: () => void
  currentAnnotation: IAnnotation | {}
  annotations: IAnnotation[]
  setAnnotation: Dispatch<React.SetStateAction<IAnnotation | undefined>>
  updateAnnotation?: (updatedAnnotation: IAnnotation) => void
  deleteAnnotation?: (annotationToDelete: IAnnotation) => void
  file: File
  projectId: number
}

export const TemplateDocumentEditor = ({
  addAnnotation,
  annotations,
  currentAnnotation,
  file,
  setAnnotation,
  updateAnnotation,
  deleteAnnotation,
  projectId,
}: EditorProps) => {
  const fileUrlRef = useRef(window.URL.createObjectURL(file))
  const { pdfDocument, setPage, page, canvasRef, triggerReload } = usePdfViewer(
    fileUrlRef.current,
  )
  const canvasDataUrl = canvasRef?.current?.toDataURL() || ''

  // Passing the component in directly violates the rule of hooks.
  // This allows the component to set itself up deterministically.
  const renderEditor = ({
    annotation,
    onChange,
    onSubmit,
  }: AnnotationEditorProps) => (
    <AnnotationEditor
      annotation={annotation}
      onChange={onChange}
      onSubmit={onSubmit}
      currentPage={page}
      projectId={projectId}
    />
  )

  const maybeUpdate = (annotation: any) =>
    updateAnnotation && updateAnnotation(annotation)
  const maybeDelete = (annotation: any) =>
    deleteAnnotation && deleteAnnotation(annotation)
  const noOp = () => undefined

  const renderContent = ({ annotation }: AnnotationEditorProps) => (
    <AnnotationEditor
      annotation={annotation}
      onChange={maybeUpdate}
      onSubmit={noOp}
      mode="update"
      onDelete={maybeDelete}
      currentPage={page}
      projectId={projectId}
    />
  )

  const isFirstPage = page === 1

  const isLastPage = page === pdfDocument?.numPages

  const goToPrevious = () => {
    triggerReload()
    setPage(page - 1)
  }
  const goToNext = () => {
    triggerReload()
    setPage(page + 1)
  }

  const currentAnnotations = annotations.filter(
    annotation => annotation.data.pageNumber === page && !annotation._destroy,
  )

  return (
    <>
      <canvas style={styles.loadingCanvas} ref={canvasRef} />
      <h2>Click and Drag to Annotate Fields</h2>
      <div style={styles.annotationContainer}>
        <Annotation
          renderEditor={renderEditor}
          renderContent={renderContent}
          src={canvasDataUrl}
          annotations={currentAnnotations}
          onChange={setAnnotation}
          value={currentAnnotation}
          onSubmit={addAnnotation}
          projectId={projectId}
        />
      </div>
      {pdfDocument?.numPages > 1 && (
        <Pagination>
          <Button disabled={isFirstPage} onClick={goToPrevious}>
            Previous
          </Button>
          <Button disabled={isLastPage} onClick={goToNext}>
            Next
          </Button>
        </Pagination>
      )}
    </>
  )
}
